import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import FetchingSpinner from "../../common/FetchingSpinner";
import {
  DashboardDonutChart,
  DashboardHorizontalBarChart,
  DashboardChartCard,
} from "../dashboard/statistics/DashboardCharts";
import { WebStorage, WebStorageNames } from "../../../utils";
import {
  appraisalStatusLabel,
  getAppraisalAdminDashboard,
  getAppraisalTeamDashboard,
} from "./appraisalApi";
import { setAppraisalAssessmentId } from "./appraisalAssessmentStorage";
import { AppraisalEmployeeCell } from "./AppraisalEmployeeCell";
import "../dashboard/statistics/dashboardStatistics.scss";
import "./Appraisal.css";

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  return Number(r);
};

const normalizeDashboard = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const kpis = raw.kpis ?? raw.Kpis ?? {};
  const pies = raw.compliancePies ?? raw.CompliancePies ?? [];
  const status = raw.statusBreakdown ?? raw.StatusBreakdown ?? [];
  const behavior = raw.behaviourAverages ?? raw.BehaviourAverages ?? raw.behaviorAverages ?? raw.BehaviorAverages ?? [];
  const team = raw.teamMembers ?? raw.TeamMembers ?? [];

  return {
    scope: raw.scope ?? raw.Scope ?? "",
    latestCompanyAppraisalId: raw.latestCompanyAppraisalId ?? raw.LatestCompanyAppraisalId ?? null,
    latestAppraisalDate: raw.latestAppraisalDate ?? raw.LatestAppraisalDate ?? null,
    officeName: raw.officeName ?? raw.OfficeName ?? null,
    isAllOffices: raw.isAllOffices ?? raw.IsAllOffices ?? true,
    kpis: {
      totalUsers: Number(kpis.totalUsers ?? kpis.TotalUsers ?? 0),
      selfSubmittedCount: Number(kpis.selfSubmittedCount ?? kpis.SelfSubmittedCount ?? 0),
      managerSubmittedCount: Number(kpis.managerSubmittedCount ?? kpis.ManagerSubmittedCount ?? 0),
      meetingConfirmedCount: Number(kpis.meetingConfirmedCount ?? kpis.MeetingConfirmedCount ?? 0),
      pendingSelfCount: Number(kpis.pendingSelfCount ?? kpis.PendingSelfCount ?? 0),
      pendingManagerCount: Number(kpis.pendingManagerCount ?? kpis.PendingManagerCount ?? 0),
      pendingMeetingCount: Number(kpis.pendingMeetingCount ?? kpis.PendingMeetingCount ?? 0),
    },
    compliancePies: (Array.isArray(pies) ? pies : []).map((p, idx) => ({
      key: p.key ?? p.Key ?? `pie-${idx}`,
      title: p.title ?? p.Title ?? "",
      slices: (p.slices ?? p.Slices ?? []).map((s, i) => ({
        id: `${p.key ?? p.Key ?? idx}-${i}`,
        name: s.name ?? s.Name ?? "—",
        count: Number(s.count ?? s.Count ?? 0),
      })),
    })),
    statusBreakdown: (Array.isArray(status) ? status : []).map((s, i) => ({
      id: `status-${i}`,
      name: s.name ?? s.Name ?? "—",
      count: Number(s.count ?? s.Count ?? 0),
    })),
    behaviorAverages: (Array.isArray(behavior) ? behavior : []).map((b) => ({
      name: b.name ?? b.Name ?? "—",
      staffAverage: Number(b.staffAverage ?? b.StaffAverage ?? 0),
      managerAverage: Number(b.managerAverage ?? b.ManagerAverage ?? 0),
      meetingAverage: Number(b.meetingAverage ?? b.MeetingAverage ?? 0),
      reviewCount: Number(b.reviewCount ?? b.ReviewCount ?? 0),
    })),
    teamMembers: (Array.isArray(team) ? team : []).map((m) => ({
      userAppraisalId: m.userAppraisalId ?? m.UserAppraisalId,
      userId: m.userId ?? m.UserId,
      userName: m.userName ?? m.UserName ?? "—",
      designation: m.designation ?? m.Designation ?? "",
      department: m.department ?? m.Department ?? "",
      officeName: m.officeName ?? m.OfficeName ?? "",
      profileImageUrl: String(m.profileImageUrl ?? m.ProfileImageUrl ?? "").trim() || null,
      status: Number(m.status ?? m.Status ?? 0),
      statusName: m.statusName ?? m.StatusName ?? "",
      appraisalDate: m.appraisalDate ?? m.AppraisalDate,
    })),
  };
};

const BehaviorAverageChart = ({ rows, t }) => {
  const max = Math.max(
    1,
    ...rows.flatMap((r) => [r.staffAverage, r.managerAverage, r.meetingAverage])
  );

  if (!rows.length) {
    return (
      <DashboardChartCard
        title={t("app.appraisal.overview.behaviorTitle", { defaultValue: "Behavior averages" })}
        icon="bi-graph-up"
      >
        <div className="dashboard-chart-empty">
          <i className="bi bi-bar-chart d-block mb-2 fs-4" />
          {t("app.appraisal.overview.noChartData", { defaultValue: "No rating data yet for this cycle." })}
        </div>
      </DashboardChartCard>
    );
  }

  return (
    <DashboardChartCard
      title={t("app.appraisal.overview.behaviorTitle", { defaultValue: "Behavior averages" })}
      icon="bi-graph-up"
    >
      <div className="appraisal-beh-legend mb-3">
        <span><i className="appraisal-beh-swatch appraisal-beh-swatch--staff" /> {t("app.appraisal.overview.staffAvg", { defaultValue: "Staff" })}</span>
        <span><i className="appraisal-beh-swatch appraisal-beh-swatch--manager" /> {t("app.appraisal.overview.managerAvg", { defaultValue: "Manager" })}</span>
        <span><i className="appraisal-beh-swatch appraisal-beh-swatch--meeting" /> {t("app.appraisal.overview.meetingAvg", { defaultValue: "Meeting" })}</span>
      </div>
      {rows.map((row) => (
        <div key={row.name} className="appraisal-beh-row">
          <div className="appraisal-beh-row__label" title={row.name}>{row.name}</div>
          <div className="appraisal-beh-row__bars">
            <div className="appraisal-beh-bar-track" title={`Staff: ${row.staffAverage}`}>
              <div
                className="appraisal-beh-bar-fill appraisal-beh-bar-fill--staff"
                style={{ width: `${Math.round((row.staffAverage / max) * 100)}%` }}
              />
            </div>
            <div className="appraisal-beh-bar-track" title={`Manager: ${row.managerAverage}`}>
              <div
                className="appraisal-beh-bar-fill appraisal-beh-bar-fill--manager"
                style={{ width: `${Math.round((row.managerAverage / max) * 100)}%` }}
              />
            </div>
            <div className="appraisal-beh-bar-track" title={`Meeting: ${row.meetingAverage}`}>
              <div
                className="appraisal-beh-bar-fill appraisal-beh-bar-fill--meeting"
                style={{ width: `${Math.round((row.meetingAverage / max) * 100)}%` }}
              />
            </div>
          </div>
          <div className="appraisal-beh-row__values">
            {row.staffAverage.toFixed(1)} / {row.managerAverage.toFixed(1)} / {row.meetingAverage.toFixed(1)}
          </div>
        </div>
      ))}
    </DashboardChartCard>
  );
};

const AppraisalOverviewPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const roleId = getRoleId();
  const isAdmin = roleId === 1 || roleId === 2;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const raw = isAdmin
        ? await getAppraisalAdminDashboard()
        : await getAppraisalTeamDashboard();
      setDashboard(normalizeDashboard(raw));
    } catch (e) {
      setDashboard(null);
      setError(e?.message || t("app.appraisal.overview.loadFailed", { defaultValue: "Failed to load appraisal dashboard." }));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, t]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = dashboard?.kpis;
  const cycleLabel = useMemo(() => {
    if (!dashboard?.latestAppraisalDate) {
      return t("app.appraisal.overview.noCycle", { defaultValue: "No active appraisal cycle" });
    }
    const d = new Date(dashboard.latestAppraisalDate);
    const dateStr = Number.isNaN(d.getTime()) ? String(dashboard.latestAppraisalDate) : d.toLocaleDateString();
    const scope = dashboard.isAllOffices
      ? t("app.appraisal.overview.allOffices", { defaultValue: "All offices" })
      : (dashboard.officeName || t("app.appraisal.overview.oneOffice", { defaultValue: "Office scoped" }));
    return `${dateStr} · ${scope}`;
  }, [dashboard, t]);

  const pieTitle = (key, fallback) => {
    if (key === "self") return t("app.appraisal.overview.pieSelf", { defaultValue: "Self assessments" });
    if (key === "manager") return t("app.appraisal.overview.pieManager", { defaultValue: "Manager assessments" });
    if (key === "meeting") return t("app.appraisal.overview.pieMeeting", { defaultValue: "Appraisal meetings" });
    return fallback;
  };

  return (
    <div className="appraisal-page appraisal-overview-page">
      <CommonPageHeader
        icon={<i className="bi bi-bar-chart-line" />}
        title={t("app.appraisal.overview.title", {
          defaultValue: isAdmin ? "Appraisal Overview" : "Team Appraisal Overview",
        })}
        description={t("app.appraisal.overview.subtitle", {
          defaultValue: isAdmin
            ? "Company compliance, status funnel, and behavior rating averages for the latest cycle."
            : "Your team's appraisal progress, compliance, and behavior rating averages.",
        })}
      />

      <div className="appraisal-overview-cycle mb-3">
        <span className="appraisal-overview-cycle__badge">
          {dashboard?.scope === "Team"
            ? t("app.appraisal.overview.scopeTeam", { defaultValue: "Team" })
            : t("app.appraisal.overview.scopeCompany", { defaultValue: "Company" })}
        </span>
        <span className="appraisal-overview-cycle__meta">{cycleLabel}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={load} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1" />
          {t("app.appraisal.overview.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>

      {error ? (
        <div className="appraisal-error">
          {error}
          <button type="button" className="btn btn-link btn-sm ms-2 p-0 align-baseline" onClick={load}>
            {t("app.appraisal.overview.retry", { defaultValue: "Retry" })}
          </button>
        </div>
      ) : null}

      {loading && !dashboard ? (
        <FetchingSpinner text={t("app.appraisal.overview.loading", { defaultValue: "Loading dashboard…" })} />
      ) : (
        <>
          <section className="appraisal-kpi-grid appraisal-overview-stats" aria-label={t("app.appraisal.overview.statsAria", { defaultValue: "Appraisal KPIs" })}>
            <div className="appraisal-kpi-card appraisal-kpi-card--neutral">
              <div className="appraisal-kpi-card__accent" aria-hidden />
              <div className="appraisal-kpi-card__inner">
                <div className="appraisal-kpi-card__top">
                  <span className="appraisal-kpi-card__icon"><i className="bi bi-people-fill" /></span>
                  <div className="appraisal-kpi-card__figures">
                    <span className="appraisal-kpi-card__value">{kpis?.totalUsers ?? 0}</span>
                    <span className="appraisal-kpi-card__label">
                      {isAdmin
                        ? t("app.appraisal.overview.kpiTotal", { defaultValue: "Active users" })
                        : t("app.appraisal.overview.kpiTeam", { defaultValue: "Team appraisals" })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="appraisal-kpi-card appraisal-kpi-card--green">
              <div className="appraisal-kpi-card__accent" aria-hidden />
              <div className="appraisal-kpi-card__inner">
                <div className="appraisal-kpi-card__top">
                  <span className="appraisal-kpi-card__icon"><i className="bi bi-person-check" /></span>
                  <div className="appraisal-kpi-card__figures">
                    <span className="appraisal-kpi-card__value">{kpis?.selfSubmittedCount ?? 0}</span>
                    <span className="appraisal-kpi-card__label">{t("app.appraisal.overview.kpiSelf", { defaultValue: "Self submitted" })}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="appraisal-kpi-card appraisal-kpi-card--indigo">
              <div className="appraisal-kpi-card__accent" aria-hidden />
              <div className="appraisal-kpi-card__inner">
                <div className="appraisal-kpi-card__top">
                  <span className="appraisal-kpi-card__icon"><i className="bi bi-clipboard-check" /></span>
                  <div className="appraisal-kpi-card__figures">
                    <span className="appraisal-kpi-card__value">{kpis?.managerSubmittedCount ?? 0}</span>
                    <span className="appraisal-kpi-card__label">{t("app.appraisal.overview.kpiManager", { defaultValue: "Manager submitted" })}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="appraisal-kpi-card appraisal-kpi-card--amber">
              <div className="appraisal-kpi-card__accent" aria-hidden />
              <div className="appraisal-kpi-card__inner">
                <div className="appraisal-kpi-card__top">
                  <span className="appraisal-kpi-card__icon"><i className="bi bi-check2-all" /></span>
                  <div className="appraisal-kpi-card__figures">
                    <span className="appraisal-kpi-card__value">{kpis?.meetingConfirmedCount ?? 0}</span>
                    <span className="appraisal-kpi-card__label">{t("app.appraisal.overview.kpiMeeting", { defaultValue: "Meetings confirmed" })}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="row g-3 mb-3">
            {(dashboard?.compliancePies ?? []).map((pie) => (
              <div className="col-12 col-md-4" key={pie.key}>
                <DashboardDonutChart
                  title={pieTitle(pie.key, pie.title)}
                  icon="bi-pie-chart"
                  items={pie.slices}
                  centerLabel={t("app.appraisal.overview.total", { defaultValue: "Total" })}
                  emptyLabel={t("app.appraisal.overview.noChartData", { defaultValue: "No rating data yet for this cycle." })}
                />
              </div>
            ))}
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12 col-lg-5">
              <DashboardHorizontalBarChart
                title={t("app.appraisal.overview.statusTitle", { defaultValue: "Status breakdown" })}
                icon="bi-list-task"
                items={dashboard?.statusBreakdown ?? []}
                emptyLabel={t("app.appraisal.overview.noChartData", { defaultValue: "No rating data yet for this cycle." })}
              />
            </div>
            <div className="col-12 col-lg-7">
              <BehaviorAverageChart rows={dashboard?.behaviorAverages ?? []} t={t} />
            </div>
          </div>

          <div className="appraisal-card appraisal-overview-recent">
            <div className="appraisal-overview-recent__head">
              <h3 className="mb-0">
                {isAdmin
                  ? t("app.appraisal.overview.recentTitle", { defaultValue: "Recent appraisals" })
                  : t("app.appraisal.overview.teamTitle", { defaultValue: "Team completion" })}
              </h3>
              {!isAdmin ? (
                <button
                  type="button"
                  className="appraisal-btn"
                  onClick={() => history.push("/appraisal/team")}
                >
                  {t("app.appraisal.overview.viewTeam", { defaultValue: "View team list" })}
                </button>
              ) : (
                <button
                  type="button"
                  className="appraisal-btn"
                  onClick={() => history.push("/appraisal/team")}
                >
                  {t("app.appraisal.overview.viewAll", { defaultValue: "View all" })}
                </button>
              )}
            </div>
            {(dashboard?.teamMembers?.length ?? 0) === 0 ? (
              <div className="appraisal-team-empty">
                <i className="bi bi-people" aria-hidden />
                <p>
                  {t("app.appraisal.overview.noTeamRows", {
                    defaultValue: "No appraisals in the latest cycle yet.",
                  })}
                </p>
              </div>
            ) : (
              <div className="appraisal-table-wrap appraisal-team-table-wrap">
                <table className="appraisal-table appraisal-team-table appraisal-overview-recent-table">
                  <thead>
                    <tr>
                      <th className="appraisal-team-col-employee">
                        {t("app.appraisal.colEmployee", { defaultValue: "Employee" })}
                      </th>
                      <th className="appraisal-overview-col-designation">
                        {t("app.appraisal.colDesignation", { defaultValue: "Designation" })}
                      </th>
                      <th className="appraisal-overview-col-department">
                        {t("app.appraisal.colDepartment", { defaultValue: "Department" })}
                      </th>
                      <th className="appraisal-overview-col-office">
                        {t("app.appraisal.colOffice", { defaultValue: "Office" })}
                      </th>
                      <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                      <th className="appraisal-team-col-date">
                        {t("app.appraisal.colDate", { defaultValue: "Date" })}
                      </th>
                      <th className="appraisal-team-col-actions">
                        {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.teamMembers.slice(0, 20).map((row) => {
                      const date = row.appraisalDate ? new Date(row.appraisalDate) : null;
                      const designation = String(row.designation || "").trim();
                      const department = String(row.department || "").trim();
                      const office = String(row.officeName || "").trim();
                      return (
                        <tr key={row.userAppraisalId}>
                          <td className="appraisal-team-col-employee">
                            <AppraisalEmployeeCell
                              name={row.userName}
                              designation={designation}
                              department={department}
                              officeName={row.officeName}
                              profileImageUrl={row.profileImageUrl}
                              showDesignation={false}
                              showOrgMeta={false}
                            />
                          </td>
                          <td className="appraisal-overview-col-designation">
                            {designation ||
                              t("app.appraisal.noDesignation", {
                                defaultValue: "No designation",
                              })}
                          </td>
                          <td className="appraisal-overview-col-department">
                            {department ||
                              t("app.appraisal.noDepartment", {
                                defaultValue: "No department",
                              })}
                          </td>
                          <td className="appraisal-overview-col-office">
                            {office ||
                              t("app.appraisal.noOffice", {
                                defaultValue: "No office",
                              })}
                          </td>
                          <td>
                            <span
                              className={`appraisal-status-pill appraisal-status-pill--${row.status}`}
                            >
                              {appraisalStatusLabel(row.status) || row.statusName}
                            </span>
                          </td>
                          <td className="appraisal-team-col-date">
                            {date && !Number.isNaN(date.getTime())
                              ? date.toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="appraisal-team-col-actions">
                            <button
                              type="button"
                              className="appraisal-btn primary appraisal-team-action"
                              onClick={() => {
                                setAppraisalAssessmentId(row.userAppraisalId);
                                history.push({
                                  pathname: "/appraisal/assessment",
                                  state: { userAppraisalId: row.userAppraisalId },
                                });
                              }}
                            >
                              <i className="bi bi-box-arrow-up-right" aria-hidden />
                              {t("app.appraisal.overview.open", { defaultValue: "Open" })}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AppraisalOverviewPage;
